/**
 * ¶2A / ¶3 / ¶4A of the listing agreement — the questions only a seller or
 * landlord can answer (V090).
 *
 * Shared because there are TWO ways to start an application and both must ask:
 * the first-time onboarding wizard (`ClientOnboarding`), and the "Start a new
 * application" panel an EXISTING client uses (`ClientHome`). The panel was
 * missed when the wizard step landed, so a returning client leased out a
 * property and was never asked its address, its rent, or its term — the
 * agreement then had nothing to print. Reported from real use.
 *
 * The fields and the request-shaping live together here for the same reason:
 * two copies of "which of these does a lease send" is exactly the drift that
 * caused the miss.
 */

import { PlacesAutocomplete, type SelectedPlace } from "@re-platform/ui";

/** The six services a client can start. Defined here rather than in each screen
 *  — it was declared identically in ClientHome and ClientOnboarding, which is
 *  how the two drifted in the first place. */
export type Service =
  "buy" | "buy_loan" | "rent" | "sell" | "lease" | "mortgage";

/** Services that convey a property interest. Texas is a COMMUNITY PROPERTY
 *  state, so a married client's spouse has an interest in what is bought, sold
 *  or leased out and must sign the representation agreement too.
 *
 *  "lease" (the LANDLORD) is here and "rent" (the tenant) is not: a tenant
 *  acquires no interest, while a landlord is granting a possessory interest in
 *  property the couple owns, which Texas Family Code §5.001 will not let one
 *  spouse do alone.
 *
 *  MUST stay in step with COMMUNITY_PROPERTY_TRANSACTIONS in document-svc
 *  `onboard.py`. Shared between both onboarding screens because it was declared
 *  in the wizard only — and the home-screen panel, which never had it, is
 *  exactly where a married landlord slipped through. */
export const COMMUNITY_PROPERTY_SERVICES = new Set<string>([
  "buy",
  "buy_loan",
  "sell",
  "lease",
]);

/** Services that put a PROPERTY on the market.
 *
 *  MUST stay in step with `_LISTING_SERVICES` in document-svc `onboard.py` —
 *  that is what decides whether the answers are STORED, so a service listed
 *  here but not there collects into a void. */
export const LISTING_SERVICES = new Set<string>(["sell", "lease"]);

export function isListingService(service: string | null): boolean {
  return service != null && LISTING_SERVICES.has(service);
}

/** Everything the two callers hold. Plain strings: these come straight from
 *  <input>, and converting early would mean re-deriving "did they type
 *  anything" from a number. */
export interface ListingAnswers {
  propertyStreet: string;
  propertyCity: string;
  propertyState: string;
  propertyZip: string;
  listingPrice: string;
  listingBeginsOn: string;
  listingEndsOn: string;
  leaseTermMin: string;
  leaseTermMax: string;
  securityDeposit: string;
}

export const EMPTY_LISTING_ANSWERS: ListingAnswers = {
  propertyStreet: "",
  propertyCity: "",
  propertyState: "",
  propertyZip: "",
  listingPrice: "",
  listingBeginsOn: "",
  listingEndsOn: "",
  leaseTermMin: "",
  leaseTermMax: "",
  securityDeposit: "",
};

/** "2026-08-05" for an <input type="date">, in the BROKERAGE's day.
 *
 *  Local rather than UTC on purpose: a listing started after 7pm Central would
 *  otherwise be dated TOMORROW, and on a contract that is the wrong date — the
 *  same reason prefill.py pins its clock to America/Chicago. */
export function isoDate(d: Date): string {
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

/** The same day-of-month N months on, clamped to the end of a short month —
 *  matching `_plus_months` in prefill.py, so the date we offer is the one the
 *  server would have derived. */
export function plusMonths(from: Date, months: number): Date {
  const d = new Date(from.getFullYear(), from.getMonth() + months, 1);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(from.getDate(), lastDay));
  return d;
}

/** The dates the rate card suggests, applied only where the client has not
 *  already answered. ¶4A is negotiated per property, so this is a starting
 *  point — but two empty date pickers tell a seller nothing about what is
 *  expected. */
export function withSuggestedTerm(
  answers: ListingAnswers,
  termMonths: number | null | undefined,
): ListingAnswers {
  const today = new Date();
  return {
    ...answers,
    listingBeginsOn: answers.listingBeginsOn || isoDate(today),
    listingEndsOn:
      answers.listingEndsOn ||
      (termMonths ? isoDate(plusMonths(today, termMonths)) : ""),
  };
}

/** True when the two ranges are back to front. Mirrors the CHECKs in V090 and
 *  the request-model validator, so the client is told while looking at the
 *  fields instead of by a 422 after pressing the button. */
export function listingProblems(a: ListingAnswers): {
  termInverted: boolean;
  leaseRangeInverted: boolean;
  any: boolean;
} {
  const termInverted = Boolean(
    a.listingBeginsOn && a.listingEndsOn && a.listingEndsOn < a.listingBeginsOn,
  );
  const leaseRangeInverted = Boolean(
    a.leaseTermMin &&
    a.leaseTermMax &&
    Number(a.leaseTermMax) < Number(a.leaseTermMin),
  );
  return {
    termInverted,
    leaseRangeInverted,
    any: termInverted || leaseRangeInverted,
  };
}

/** The `self-onboard` body fragment. Empty object for a non-listing service, so
 *  a caller can spread it unconditionally. */
export function listingRequestBody(
  service: Service | null,
  a: ListingAnswers,
): Record<string, unknown> {
  if (!isListingService(service)) return {};
  const isLease = service === "lease";
  return {
    ...(a.propertyStreet.trim()
      ? {
          property_street: a.propertyStreet.trim(),
          property_city: a.propertyCity.trim(),
          property_state: a.propertyState.trim().toUpperCase(),
          property_zip: a.propertyZip.trim(),
        }
      : {}),
    ...(a.listingPrice.trim() ? { listing_price: a.listingPrice.trim() } : {}),
    ...(a.listingBeginsOn ? { listing_begins_on: a.listingBeginsOn } : {}),
    ...(a.listingEndsOn ? { listing_ends_on: a.listingEndsOn } : {}),
    ...(isLease && a.leaseTermMin.trim()
      ? { lease_term_min_months: Number(a.leaseTermMin) }
      : {}),
    ...(isLease && a.leaseTermMax.trim()
      ? { lease_term_max_months: Number(a.leaseTermMax) }
      : {}),
    // Left blank the agreement takes one month's rent, the customary Texas
    // figure — so this is only sent when the landlord wants a different one.
    ...(isLease && a.securityDeposit.trim()
      ? { security_deposit: a.securityDeposit.trim() }
      : {}),
  };
}

export function ListingDetailsFields({
  service,
  answers,
  onChange,
}: {
  service: Service | null;
  answers: ListingAnswers;
  onChange: (next: ListingAnswers) => void;
}) {
  const isLease = service === "lease";
  const problems = listingProblems(answers);
  const set = (patch: Partial<ListingAnswers>) =>
    onChange({ ...answers, ...patch });

  return (
    <>
      {/* ⚠️ NOT the client's home address, which ¶1 of the agreement prints. A
          landlord who does not live in the unit has two different addresses,
          and printing the wrong one names the wrong property on a contract.

          Autocompleted, because this address IS the property on a signed
          listing agreement (¶2A) — a typo here misnames the land the contract
          concerns. Picking a suggestion fills City/State/ZIP below; all three
          stay editable, because with no API key the field degrades to plain
          text and nothing would ever fill them. */}
      <label className="co-field">
        <span>Property address</span>
        <PlacesAutocomplete
          apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
          className=""
          value={answers.propertyStreet}
          onChange={(v) => set({ propertyStreet: v })}
          onSelect={(p: SelectedPlace) =>
            set({
              // Street only — the formatted address repeats the city/state/ZIP
              // that have their own boxes on the form, and ¶2A would print
              // "McKinney, TX 75070" twice.
              propertyStreet: p.street || p.address,
              ...(p.city ? { propertyCity: p.city } : {}),
              ...(p.state ? { propertyState: p.state } : {}),
              ...(p.zip ? { propertyZip: p.zip } : {}),
            })
          }
          placeholder="4701 Rancho Del Norte"
        />
      </label>
      <div className="co-field-row">
        <label className="co-field">
          <span>City</span>
          <input
            value={answers.propertyCity}
            onChange={(e) => set({ propertyCity: e.target.value })}
            placeholder="McKinney"
          />
        </label>
        <label className="co-field">
          <span>State</span>
          <input
            value={answers.propertyState}
            onChange={(e) => set({ propertyState: e.target.value })}
            placeholder="TX"
            maxLength={2}
          />
        </label>
        <label className="co-field">
          <span>ZIP</span>
          <input
            value={answers.propertyZip}
            onChange={(e) => set({ propertyZip: e.target.value })}
            placeholder="75070"
          />
        </label>
      </div>

      {/* ¶3 (sale) or ¶3A (monthly rental) — one question, two meanings. */}
      <label className="co-field">
        <span>{isLease ? "Monthly rent" : "Listing price"}</span>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={answers.listingPrice}
          onChange={(e) => set({ listingPrice: e.target.value })}
          placeholder={isLease ? "2590.00" : "495000.00"}
        />
      </label>

      {isLease && (
        <>
          {/* ¶3B — the range of lease terms the landlord will accept. */}
          <div className="co-field-row">
            <label className="co-field">
              <span>Shortest lease you'll accept (months)</span>
              <input
                type="number"
                inputMode="numeric"
                min="1"
                value={answers.leaseTermMin}
                onChange={(e) => set({ leaseTermMin: e.target.value })}
                placeholder="12"
              />
            </label>
            <label className="co-field">
              <span>Longest (months)</span>
              <input
                type="number"
                inputMode="numeric"
                min="1"
                value={answers.leaseTermMax}
                onChange={(e) => set({ leaseTermMax: e.target.value })}
                placeholder="24"
              />
            </label>
          </div>
          {/* ¶20D. Blank means one month's rent, which is customary in Texas. */}
          <label className="co-field">
            <span>Security deposit</span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={answers.securityDeposit}
              onChange={(e) => set({ securityDeposit: e.target.value })}
              placeholder={
                answers.listingPrice.trim()
                  ? `${answers.listingPrice.trim()} (one month's rent)`
                  : "One month's rent"
              }
            />
          </label>
        </>
      )}

      {/* ¶4A — how long the brokerage may market the property. */}
      <div className="co-field-row">
        <label className="co-field">
          <span>Listing starts</span>
          <input
            type="date"
            value={answers.listingBeginsOn}
            onChange={(e) => set({ listingBeginsOn: e.target.value })}
          />
        </label>
        <label className="co-field">
          <span>Listing ends</span>
          <input
            type="date"
            value={answers.listingEndsOn}
            onChange={(e) => set({ listingEndsOn: e.target.value })}
          />
        </label>
      </div>
      {problems.termInverted && (
        <p className="co-error">The listing cannot end before it begins.</p>
      )}
      {problems.leaseRangeInverted && (
        <p className="co-error">
          The longest lease term must be at least the shortest.
        </p>
      )}
    </>
  );
}
